import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate } from '@tanstack/react-router'
import {
  Brain,
  MessageSquare,
  Sparkles,
  TrendingDown,
  Check,
  X,
  ChevronDown,
  ExternalLink,
  RefreshCw,
  Search,
  AlertTriangle,
  ClipboardCheck,
  Clock,
} from 'lucide-react'
import { api, apiFetchJson } from '@/lib/api-client'
import { formatDateTime } from '@/lib/utils'
import { PageHeader } from '@/components/ui/page-header'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { EmptyState } from '@/components/ui/empty-state'
import { formatCurrency, LICENSE_BILLING_PLAN_INFO, type LicenseBillingPlan } from '@roi365/shared'
import { useAuth } from '@/hooks/use-auth'
import { useCanWrite } from '@/hooks/use-permissions'
import { FindingChatPanel } from '@/components/ai/finding-chat-panel'
import {
  actionLabels,
  categoryLabels,
  evidenceSourceLabels,
  findingKindLabels,
  humanizeFindingText,
  findingKindVariant,
  parseJsonArray,
  parseJsonObject,
  researchKindLabels,
  runStatusLabels,
  runStatusVariant,
  savingStatusLabels,
  savingStatusVariant,
  taskStatusLabels,
} from '@/lib/ai-labels'
import { toast } from 'sonner'
import { useEffect, useState } from 'react'

/* ── Types (API rows are snake_case) ─────────────── */

interface Finding {
  id: string
  m365_tenant_id: string
  category: string
  status: string
  title: string
  description: string
  severity: string
  confidence: number | null
  estimated_monthly_saving: number
  recommended_action: string | null
  finding_kind: string | null
  evidence_level: string | null
  research_task_key: string | null
  subjects_json: string
  current_state: string | null
  proposal: string | null
  affected_units: number | null
  capabilities_required_json: string
  capabilities_to_keep_json: string
  services_at_risk_json: string
  inferences_json: string
  risks_json: string
  dependencies_json: string
  missing_data_json: string
  sources_json: string
  saving_assumptions_json: string
  related_rule_ids_json: string
  saving_status: string
  potential_monthly_saving: number | null
  realizable_monthly_saving: number | null
  saving_basis_json: string | null
  confidence_reason: string | null
  next_step: string | null
  proposed_sku_part_number: string | null
  data_as_of: string | null
  sources_checked_at: string | null
  client_reviewed_at: string | null
  client_review_note: string | null
  beyond_rules: string | null
  evidence_json: string | null
  license_display_name: string | null
  license_billing_plan: string | null
  license_sku_id: string | null
  notes: string | null
  created_at: string
}

/** Contracted licenses by SKU part number: custom name (always preferred) and billing plan. */
type SkuInfoMap = Map<string, { name: string; billingPlan: string | null }>

function billingPlanLabel(plan: string | null | undefined): string | null {
  if (!plan || !(plan in LICENSE_BILLING_PLAN_INFO)) return null
  const info = LICENSE_BILLING_PLAN_INFO[plan as LicenseBillingPlan]
  return `${info.label} (compromiso ${info.commitment.toLowerCase()}, pago ${info.payment.toLowerCase()})`
}

/** Custom display name of a SKU when the client set one, else the SKU part number as it came. */
function skuName(skuInfo: SkuInfoMap, ref: string): string {
  return skuInfo.get(ref.toUpperCase())?.name ?? ref
}

interface Overview {
  totals: {
    verifiedMonthly: number
    estimatedMonthly: number
    pendingValuation: number
    overlappingLines: number
    groups: number
    findings: number
    reviewed: number
    byKind: Record<string, number>
    bySaving: Record<string, number>
  }
  dataAsOf: string | null
  sourcesCheckedAt: string | null
  lastRun: {
    id: string
    status: string
    trigger: string
    created_at: string
    completed_at: string | null
    tasks_total: number
    tasks_completed: number
    tasks_failed: number
    error_log: string | null
    openTasks: number
    totalTasks: number
  } | null
}

interface Opportunity {
  taskKey: string
  kind: string
  source: string
  subjects: Array<{ type: string; ref: string; count: number | null }>
  relatedRuleCount: number
  rationale: string
  last: { status: string; completed_at: string | null; recommendation_id: string | null; error_code: string | null } | null
}

interface InsightsResponse {
  summary: string | null
  topPriorities?: string[]
  riskAreas?: string[]
  top_priorities?: string[]
  risk_areas?: string[]
}

interface SavingBasis {
  action: string
  current: { skuPartNumber: string; displayName: string | null; price: number | null; source: string | null } | null
  proposed: { skuPartNumber: string; displayName: string | null; price: number | null; source: string | null; availableUnits?: number } | null
  units_source: string
  note: string
  economics_note?: string
}

interface Source {
  url: string
  title: string
  publisher: string
  accessed_at: string
  claim: string
  verified: boolean
  official: boolean
}

/* ── Small pieces ───────────────────────────────── */

function ConfidenceBar({ score }: { score: number }) {
  const pct = Math.round(score * 100)
  let barColor = 'var(--color-error)'
  if (score >= 0.8) barColor = 'var(--color-success)'
  else if (score >= 0.5) barColor = 'var(--color-warning)'
  return (
    <div className="flex items-center gap-2">
      <span className="text-xs font-medium tabular-nums" style={{ color: 'var(--color-text-secondary)' }}>{pct}%</span>
      <div className="h-1.5 w-16 overflow-hidden rounded-full" style={{ backgroundColor: 'var(--color-gray-200)' }}>
        <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: barColor }} />
      </div>
    </div>
  )
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h5 className="mb-1 text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--color-text-tertiary)' }}>{title}</h5>
      <div className="text-sm" style={{ color: 'var(--color-text-primary)' }}>{children}</div>
    </div>
  )
}

function BulletList({ items, empty = '—', skuInfo }: { items: string[]; empty?: string; skuInfo?: SkuInfoMap }) {
  if (items.length === 0) return <p style={{ color: 'var(--color-text-tertiary)' }}>{empty}</p>
  return (
    <ul className="list-disc space-y-0.5 pl-5">
      {items.map((it, i) => <li key={i}>{humanizeFindingText(it, skuInfo)}</li>)}
    </ul>
  )
}

const selectClass = 'rounded-md border px-2 py-1.5 text-xs'
const selectStyle = { borderColor: 'var(--color-border)', backgroundColor: 'var(--color-surface)' }

/* ── Page ───────────────────────────────────────── */

export function AiRecommendationsPage() {
  const queryClient = useQueryClient()
  const { user } = useAuth()
  const canWrite = useCanWrite()
  const canGenerate = user?.role === 'tenant_admin' || user?.role === 'super_admin'

  const [filters, setFilters] = useState({ findingKind: '', savingStatus: '', status: 'open', relation: '', category: '', minConfidence: '', minSaving: '', evidence: '' })
  const [page, setPage] = useState(1)
  const [expanded, setExpanded] = useState<Set<string>>(new Set())
  const navigate = useNavigate()
  // Deep link (/ai-recommendations?finding=<id>) from /recommendations or from an opportunity: that finding is pinned above the list.
  const [pinnedId, setPinnedId] = useState<string | null>(() => new URLSearchParams(window.location.search).get('finding'))
  // "Preguntar al investigador": finding whose thread panel is open.
  const [chatFinding, setChatFinding] = useState<{ id: string | null; title: string } | null>(null)
  const pinFinding = (id: string | null) => {
    setPinnedId(id)
    navigate({ to: '/ai-recommendations', search: id ? { finding: id } : {} })
    if (id) window.scrollTo({ top: 0, behavior: 'smooth' })
  }
  useEffect(() => { if (pinnedId) setExpanded((prev) => new Set(prev).add(pinnedId)) }, [pinnedId])
  const perPage = 20

  const setFilter = (k: keyof typeof filters, v: string) => { setFilters((f) => ({ ...f, [k]: v })); setPage(1) }

  const { data: aiStatus, isLoading: statusLoading } = useQuery({
    queryKey: ['ai-recommendations', 'status'],
    queryFn: () => api.get<{ enabled: boolean; visibilityMode: string; configured: boolean; agentVerified: boolean }>('/ai-recommendations/status'),
    retry: false,
  })
  const enabled = aiStatus?.enabled === true
  const { data: licenses } = useQuery({
    enabled,
    queryKey: ['settings', 'licenses'],
    queryFn: () => api.get<Array<{ sku_part_number: string; display_name: string; custom_display_name: string | null; billing_plan: string | null }>>('/licenses'),
  })
  const skuInfo: SkuInfoMap = new Map((licenses ?? []).map((l) => [l.sku_part_number.toUpperCase(), { name: l.custom_display_name || l.display_name, billingPlan: l.billing_plan }]))

  const { data: insights } = useQuery({
    enabled,
    queryKey: ['ai-recommendations', 'insights'],
    queryFn: () => api.get<InsightsResponse | null>('/ai-recommendations/insights'),
  })

  const { data: overview, isError: overviewError, refetch: refetchOverview } = useQuery({
    enabled,
    queryKey: ['ai-recommendations', 'overview'],
    queryFn: () => api.get<Overview>('/ai-recommendations/overview'),
    refetchInterval: (q) => {
      const s = q.state.data?.lastRun?.status
      return s === 'queued' || s === 'running' ? 10_000 : false
    },
  })
  const running = overview?.lastRun?.status === 'queued' || overview?.lastRun?.status === 'running'

  const { data: findingsRaw, isLoading: findingsLoading, isError: findingsError, refetch: refetchFindings } = useQuery({
    enabled,
    queryKey: ['ai-recommendations', 'findings', filters, page],
    queryFn: async () => {
      const params = new URLSearchParams({ page: String(page), perPage: String(perPage) })
      for (const [k, v] of Object.entries(filters)) if (v) params.set(k, v)
      return apiFetchJson<{ ok: boolean; data: Finding[]; meta: { total: number; totalPages: number } }>(`/ai-recommendations?${params}`)
    },
  })

  const { data: pinned, isLoading: pinnedLoading, isError: pinnedError } = useQuery({
    enabled: enabled && !!pinnedId,
    queryKey: ['ai-recommendations', 'finding', pinnedId],
    queryFn: () => api.get<Finding>(`/ai-recommendations/${pinnedId}`),
    retry: false,
  })
  const { data: opportunities, isLoading: oppsLoading } = useQuery({
    enabled: enabled && canGenerate,
    queryKey: ['ai-recommendations', 'opportunities'],
    queryFn: () => api.get<Opportunity[]>('/ai-recommendations/opportunities'),
  })

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['ai-recommendations'] })
  const onErr = (err: unknown) => toast.error((err as Error).message)

  const generateMutation = useMutation({
    mutationFn: () => api.post('/ai-recommendations/generate'),
    onSuccess: () => { toast.success('Análisis encolado. Se actualizará automáticamente.'); invalidate() },
    onError: onErr,
  })
  const investigateMutation = useMutation({
    mutationFn: (taskKey: string) => api.post('/ai-recommendations/investigate', { taskKey }),
    onSuccess: () => { toast.success('Investigación encolada'); invalidate() },
    onError: onErr,
  })
  const reinvestigateMutation = useMutation({
    mutationFn: (id: string) => api.post(`/ai-recommendations/${id}/reinvestigate`),
    onSuccess: () => { toast.success('Repetición del análisis encolada'); invalidate() },
    onError: onErr,
  })
  const reviewMutation = useMutation({
    mutationFn: (id: string) => api.put(`/ai-recommendations/${id}/review`, { note: null }),
    onSuccess: () => { toast.success('Hallazgo marcado como revisado'); invalidate() },
    onError: onErr,
  })
  const promoteMutation = useMutation({
    mutationFn: (id: string) => api.put(`/ai-recommendations/${id}/promote`),
    onSuccess: () => { toast.success('Hallazgo promovido a Recomendaciones'); invalidate(); queryClient.invalidateQueries({ queryKey: ['recommendations'] }) },
    onError: onErr,
  })
  const applyMutation = useMutation({
    mutationFn: (id: string) => api.put(`/ai-recommendations/${id}/accept`),
    onSuccess: () => { toast.success('Hallazgo marcado como aplicado'); invalidate(); queryClient.invalidateQueries({ queryKey: ['recommendations'] }) },
    onError: onErr,
  })
  const dismissMutation = useMutation({
    mutationFn: (id: string) => api.put(`/ai-recommendations/${id}/dismiss`),
    onSuccess: () => { toast.success('Hallazgo descartado'); invalidate() },
    onError: onErr,
  })

  const findings = findingsRaw?.data ?? []
  const totalPages = findingsRaw?.meta?.totalPages ?? 1

  const rawInsights = insights as (InsightsResponse & { top_priorities?: string[]; risk_areas?: string[] }) | null | undefined
  const normalizedInsights = rawInsights
    ? { summary: rawInsights.summary ?? null, topPriorities: rawInsights.topPriorities ?? rawInsights.top_priorities ?? [], riskAreas: rawInsights.riskAreas ?? rawInsights.risk_areas ?? [] }
    : null

  const toggle = (id: string) => setExpanded((prev) => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n })

  if (statusLoading) {
    return <div className="space-y-6"><div className="h-16 animate-pulse rounded-lg" style={{ backgroundColor: 'var(--color-gray-200)' }} /></div>
  }

  if (!enabled) {
    return (
      <div className="space-y-6">
        <PageHeader title="Pagina no encontrada" description="" />
        <Card>
          <EmptyState icon={Brain} title="Funcionalidad no disponible" description="La investigación IA no está habilitada para tu cuenta. Contacta con el administrador." />
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Investigación IA de licencias"
        description="Oportunidades investigadas con fuentes actuales, complementarias a las recomendaciones por reglas"
        actions={(
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="secondary" onClick={() => setChatFinding({ id: null, title: 'Licenciamiento Microsoft 365 de vuestra organización' })} disabled={!aiStatus?.configured}>
              <MessageSquare className="h-4 w-4" /> Preguntar al investigador
            </Button>
            {canGenerate && (
              <Button onClick={() => generateMutation.mutate()} loading={generateMutation.isPending} disabled={running || !aiStatus?.configured}>
                <Sparkles className="h-4 w-4" /> {running ? 'Análisis en curso' : 'Iniciar análisis'}
              </Button>
            )}
          </div>
        )}
      />

      {/* ── Resumen inicial (deterministic insights card, unchanged) ── */}
      {normalizedInsights && (normalizedInsights.summary || normalizedInsights.topPriorities.length > 0 || normalizedInsights.riskAreas.length > 0) && (
        <Card>
          <div className="flex items-start gap-4">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg" style={{ backgroundColor: 'var(--color-brand-primary-50)', color: 'var(--color-brand-primary)' }}>
              <Brain className="h-5 w-5" />
            </div>
            <div className="min-w-0 flex-1 space-y-3">
              {normalizedInsights.summary && <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>{normalizedInsights.summary}</p>}
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                {normalizedInsights.topPriorities.length > 0 && (
                  <div>
                    <h4 className="mb-1 text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--color-text-tertiary)' }}>Prioridades principales</h4>
                    <ul className="space-y-1">
                      {normalizedInsights.topPriorities.map((p, i) => (
                        <li key={i} className="flex items-start gap-1.5 text-sm" style={{ color: 'var(--color-text-primary)' }}>
                          <Sparkles className="mt-0.5 h-3.5 w-3.5 shrink-0" style={{ color: 'var(--color-brand-secondary)' }} />{p}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {normalizedInsights.riskAreas.length > 0 && (
                  <div>
                    <h4 className="mb-1 text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--color-text-tertiary)' }}>Areas de riesgo</h4>
                    <ul className="space-y-1">
                      {normalizedInsights.riskAreas.map((r, i) => (
                        <li key={i} className="flex items-start gap-1.5 text-sm" style={{ color: 'var(--color-text-primary)' }}>
                          <TrendingDown className="mt-0.5 h-3.5 w-3.5 shrink-0" style={{ color: 'var(--color-warning)' }} />{r}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
              <p className="text-xs" style={{ color: 'var(--color-text-tertiary)' }}>Resumen calculado con los datos sincronizados del tenant, sin intervención de IA.</p>
            </div>
          </div>
        </Card>
      )}

      {/* ── Not configured ── */}
      {!aiStatus?.configured && (
        <Card>
          <div className="flex items-start gap-3">
            <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" style={{ color: 'var(--color-warning)' }} />
            <div className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
              <p className="font-medium" style={{ color: 'var(--color-text-primary)' }}>El investigador de IA no está configurado para tu cuenta.</p>
              <p>Un administrador de ROI365 debe vincular el agente de investigación. Mientras tanto se muestran los hallazgos anteriores, si los hay.</p>
            </div>
          </div>
        </Card>
      )}

      {/* ── Analysis state ── */}
      {overviewError ? (
        <Card>
          <div className="flex items-center justify-between gap-3 text-sm">
            <span style={{ color: 'var(--color-error)' }}>No se pudo cargar el estado del análisis.</span>
            <Button variant="secondary" size="sm" onClick={() => refetchOverview()}><RefreshCw className="h-3.5 w-3.5" /> Reintentar</Button>
          </div>
        </Card>
      ) : overview && (
        <Card>
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4" style={{ color: 'var(--color-text-tertiary)' }} />
              <span style={{ color: 'var(--color-text-secondary)' }}>Estado del análisis:</span>
              {overview.lastRun ? (
                <>
                  <Badge variant={runStatusVariant[overview.lastRun.status] ?? 'neutral'}>{runStatusLabels[overview.lastRun.status] ?? overview.lastRun.status}</Badge>
                  {running && <span className="text-xs tabular-nums" style={{ color: 'var(--color-text-tertiary)' }}>{overview.lastRun.totalTasks - overview.lastRun.openTasks}/{overview.lastRun.totalTasks} investigaciones</span>}
                  <span className="text-xs tabular-nums" style={{ color: 'var(--color-text-tertiary)' }}>{formatDateTime(overview.lastRun.completed_at ?? overview.lastRun.created_at)}</span>
                </>
              ) : <span style={{ color: 'var(--color-text-tertiary)' }}>sin análisis todavía</span>}
            </div>
            <div><span style={{ color: 'var(--color-text-secondary)' }}>Datos del tenant: </span><span className="tabular-nums">{formatDateTime(overview.dataAsOf)}</span></div>
            <div><span style={{ color: 'var(--color-text-secondary)' }}>Fuentes consultadas: </span><span className="tabular-nums">{formatDateTime(overview.sourcesCheckedAt)}</span></div>
            {overview.lastRun?.error_log && <span className="text-xs" style={{ color: 'var(--color-error)' }}>{overview.lastRun.error_log}</span>}
          </div>
        </Card>
      )}

      {/* ── 1. Findings ── */}
      <div>
        <h3 className="text-base font-semibold" style={{ color: 'var(--color-text-primary)' }}>1. Hallazgos del investigador</h3>
        <p className="text-xs" style={{ color: 'var(--color-text-tertiary)' }}>Conclusiones de cada investigación, con evidencia del tenant, fuentes externas y ahorro calculado por ROI365. Salen de las señales del apartado 2.</p>
      </div>

      {pinnedId && (
        <Card>
          <div className="mb-3 flex items-center justify-between gap-3">
            <p className="text-sm font-medium" style={{ color: 'var(--color-text-primary)' }}>Mostrando solo el hallazgo enlazado</p>
            <Button variant="secondary" size="sm" onClick={() => pinFinding(null)}><X className="h-3.5 w-3.5" /> Ver todos los hallazgos</Button>
          </div>
          {pinnedLoading ? (
            <div className="h-32 animate-pulse rounded-lg" style={{ backgroundColor: 'var(--color-gray-200)' }} />
          ) : pinnedError || !pinned ? (
            <p className="text-sm" style={{ color: 'var(--color-text-tertiary)' }}>El hallazgo no existe o ya no es visible para tu cuenta.</p>
          ) : (
            <FindingCard
              f={pinned}
              skuInfo={skuInfo}
              expanded={expanded.has(pinned.id)}
              onToggle={() => toggle(pinned.id)}
              canWrite={canWrite}
              canGenerate={canGenerate && !running}
              busy={{
                review: reviewMutation.isPending && reviewMutation.variables === pinned.id,
                promote: promoteMutation.isPending && promoteMutation.variables === pinned.id,
                apply: applyMutation.isPending && applyMutation.variables === pinned.id,
                dismiss: dismissMutation.isPending && dismissMutation.variables === pinned.id,
                reinvestigate: reinvestigateMutation.isPending && reinvestigateMutation.variables === pinned.id,
              }}
              onReview={() => reviewMutation.mutate(pinned.id)}
              onPromote={() => promoteMutation.mutate(pinned.id)}
              onApply={() => applyMutation.mutate(pinned.id)}
              onDismiss={() => dismissMutation.mutate(pinned.id)}
              onReinvestigate={() => reinvestigateMutation.mutate(pinned.id)}
              onAsk={() => setChatFinding({ id: pinned.id, title: pinned.title })}
            />
          )}
        </Card>
      )}

      {/* Filters and list are hidden while a finding is pinned: the link acts as a filter to that single finding. */}
      {!pinnedId && (<>
      {/* ── Filters ── */}
      <div className="flex flex-wrap items-center gap-2">
        <select className={selectClass} style={selectStyle} value={filters.status} onChange={(e) => setFilter('status', e.target.value)}>
          <option value="open">Abiertos</option><option value="reviewed">Revisados</option><option value="accepted">Promovidos</option><option value="applied">Aplicados</option><option value="dismissed">Descartados</option><option value="all">Todos</option>
        </select>
        <select className={selectClass} style={selectStyle} value={filters.findingKind} onChange={(e) => setFilter('findingKind', e.target.value)}>
          <option value="">Tipo: todos</option>{Object.entries(findingKindLabels).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <select className={selectClass} style={selectStyle} value={filters.savingStatus} onChange={(e) => setFilter('savingStatus', e.target.value)}>
          <option value="">Ahorro: todos</option>{Object.entries(savingStatusLabels).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <select className={selectClass} style={selectStyle} value={filters.relation} onChange={(e) => setFilter('relation', e.target.value)}>
          <option value="">Relación con reglas: todas</option><option value="with_rule">Relacionados con una regla</option><option value="without_rule">Sin regla asociada</option>
        </select>
        <select className={selectClass} style={selectStyle} value={filters.category} onChange={(e) => setFilter('category', e.target.value)}>
          <option value="">Categoría: todas</option>{Object.entries(categoryLabels).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <select className={selectClass} style={selectStyle} value={filters.minConfidence} onChange={(e) => setFilter('minConfidence', e.target.value)}>
          <option value="">Confianza: cualquiera</option><option value="0.9">≥ 90%</option><option value="0.8">≥ 80%</option><option value="0.7">≥ 70%</option>
        </select>
        <select className={selectClass} style={selectStyle} value={filters.evidence} onChange={(e) => setFilter('evidence', e.target.value)}>
          <option value="">Evidencia: toda</option><option value="solid">Sólida</option><option value="weak">Insuficiente: requiere validación</option>
        </select>
        <select className={selectClass} style={selectStyle} value={filters.minSaving} onChange={(e) => setFilter('minSaving', e.target.value)}>
          <option value="">Impacto: cualquiera</option><option value="50">≥ 50 €/mes</option><option value="200">≥ 200 €/mes</option><option value="1000">≥ 1.000 €/mes</option>
        </select>
      </div>

      {/* ── Findings ── */}
      {findingsLoading ? (
        <div className="space-y-3">{[1, 2, 3].map((i) => <div key={i} className="h-32 animate-pulse rounded-lg" style={{ backgroundColor: 'var(--color-gray-200)' }} />)}</div>
      ) : findingsError ? (
        <Card>
          <div className="flex items-center justify-between gap-3 text-sm">
            <span style={{ color: 'var(--color-error)' }}>No se pudieron cargar los hallazgos.</span>
            <Button variant="secondary" size="sm" onClick={() => refetchFindings()}><RefreshCw className="h-3.5 w-3.5" /> Reintentar</Button>
          </div>
        </Card>
      ) : findings.length === 0 ? (
        <Card>
          <EmptyState
            icon={Brain}
            title={overview?.lastRun ? 'Sin hallazgos con estos filtros' : 'Todavía no hay investigaciones'}
            description={overview?.lastRun ? 'Cambia los filtros o repite el análisis para obtener nuevas oportunidades.' : canGenerate ? 'Inicia un análisis para investigar las oportunidades detectadas en tu tenant.' : 'Un administrador de tu cuenta puede iniciar el primer análisis.'}
          />
        </Card>
      ) : (
        <div className="space-y-3">
          {findings.map((f) => (
            <FindingCard
              key={f.id}
              f={f}
              skuInfo={skuInfo}
              expanded={expanded.has(f.id)}
              onToggle={() => toggle(f.id)}
              canWrite={canWrite}
              canGenerate={canGenerate && !running}
              busy={{
                review: reviewMutation.isPending && reviewMutation.variables === f.id,
                promote: promoteMutation.isPending && promoteMutation.variables === f.id,
                apply: applyMutation.isPending && applyMutation.variables === f.id,
                dismiss: dismissMutation.isPending && dismissMutation.variables === f.id,
                reinvestigate: reinvestigateMutation.isPending && reinvestigateMutation.variables === f.id,
              }}
              onReview={() => reviewMutation.mutate(f.id)}
              onPromote={() => promoteMutation.mutate(f.id)}
              onApply={() => applyMutation.mutate(f.id)}
              onDismiss={() => dismissMutation.mutate(f.id)}
              onReinvestigate={() => reinvestigateMutation.mutate(f.id)}
              onAsk={() => setChatFinding({ id: f.id, title: f.title })}
            />
          ))}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 pt-4">
              <Button variant="secondary" size="sm" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>Anterior</Button>
              <span className="text-sm tabular-nums" style={{ color: 'var(--color-text-secondary)' }}>Pagina {page} de {totalPages}</span>
              <Button variant="secondary" size="sm" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>Siguiente</Button>
            </div>
          )}
        </div>
      )}
      </>)}

      {chatFinding && <FindingChatPanel findingId={chatFinding.id} findingTitle={chatFinding.title} canWrite={canWrite} onClose={() => setChatFinding(null)} />}

      {/* ── 2. Opportunities (candidates) ── */}
      {canGenerate && (
        <Card>
          <div className="mb-3 flex items-center justify-between">
            <div>
              <h3 className="text-base font-semibold" style={{ color: 'var(--color-text-primary)' }}>2. Señales a investigar</h3>
              <p className="text-xs" style={{ color: 'var(--color-text-tertiary)' }}>De aquí salen los hallazgos del apartado 1. Son señales calculadas con los datos del tenant, sin IA y sin coste: cada "Investigar" abre una sesión del agente que puede acabar en un hallazgo, en revisión manual o rechazada.</p>
            </div>
          </div>
          {oppsLoading ? (
            <div className="h-20 animate-pulse rounded" style={{ backgroundColor: 'var(--color-gray-100)' }} />
          ) : !opportunities || opportunities.length === 0 ? (
            <p className="text-sm" style={{ color: 'var(--color-text-tertiary)' }}>No se detectan oportunidades con los datos actuales.</p>
          ) : (
            <div className="divide-y" style={{ borderColor: 'var(--color-border)' }}>
              {opportunities.map((o) => (
                <div key={o.taskKey} className="flex items-start justify-between gap-4 py-3">
                  <div className="min-w-0 space-y-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge variant={o.source === 'rule' ? 'info' : 'premium'}>{researchKindLabels[o.kind] ?? o.kind}</Badge>
                      {o.subjects.map((s, i) => <span key={i} className="text-xs font-medium" style={{ color: 'var(--color-brand-secondary)' }}>{s.type === 'sku' ? skuName(skuInfo, s.ref) : s.ref}{s.count !== null ? ` (${s.count})` : ''}</span>)}
                      {o.relatedRuleCount > 0 && <span className="text-xs" style={{ color: 'var(--color-text-tertiary)' }}>{o.relatedRuleCount} regla(s) relacionada(s)</span>}
                      {o.last && <Badge variant="neutral">{taskStatusLabels[o.last.status] ?? o.last.status}{o.last.completed_at ? ` · ${formatDateTime(o.last.completed_at)}` : ''}</Badge>}
                      {o.last?.recommendation_id && (
                        <button type="button" className="inline-flex items-center gap-1 text-xs underline" style={{ color: 'var(--color-text-link)' }} onClick={() => pinFinding(o.last!.recommendation_id!)}>
                          <Brain className="h-3.5 w-3.5" /> Ver hallazgo
                        </button>
                      )}
                    </div>
                    <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>{o.rationale}</p>
                  </div>
                  <Button variant="secondary" size="sm" disabled={running || !aiStatus?.configured} loading={investigateMutation.isPending && investigateMutation.variables === o.taskKey} onClick={() => investigateMutation.mutate(o.taskKey)}>
                    <Search className="h-3.5 w-3.5" /> {o.last ? 'Repetir' : 'Investigar'}
                  </Button>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}
    </div>
  )
}

/* ── Finding card ───────────────────────────────── */

interface FindingCardProps {
  f: Finding
  skuInfo: SkuInfoMap
  expanded: boolean
  onToggle: () => void
  canWrite: boolean
  canGenerate: boolean
  busy: { review: boolean; promote: boolean; apply: boolean; dismiss: boolean; reinvestigate: boolean }
  onReview: () => void
  onPromote: () => void
  onApply: () => void
  onDismiss: () => void
  onReinvestigate: () => void
  onAsk: () => void
}

function FindingCard({ f, skuInfo, expanded, onToggle, canWrite, canGenerate, busy, onReview, onPromote, onApply, onDismiss, onReinvestigate, onAsk }: FindingCardProps) {
  const kind = f.finding_kind ?? 'legacy'
  // Client decisions (promote / applied / dismiss) only from 'published': same rule as the API.
  const canDecide = f.status === 'published'
  // "Revisado" is a read receipt: allowed on any open finding the client can see.
  const canMarkReviewed = ['published', 'draft', 'approved', 'pending_review'].includes(f.status) && !f.client_reviewed_at
  const adminPendingLabel: Record<string, string> = { pending_review: 'Pendiente de revisión por administrador', draft: 'Borrador: pendiente de publicación por administrador', approved: 'Aprobado: pendiente de publicación por administrador' }
  const isPendingReview = f.status in adminPendingLabel
  const subjects = parseJsonArray<{ type: string; ref: string; count: number | null }>(f.subjects_json)
  const relatedIds = parseJsonArray<string>(f.related_rule_ids_json)
  const plain = (t: string | null | undefined) => humanizeFindingText(t, skuInfo)
  const weakEvidence = f.evidence_level === 'weak'
  // Billing plan of the affected SKU: the linked license first, else the subject SKUs (deduplicated).
  const billingPlans = Array.from(new Set(
    (f.license_billing_plan ? [f.license_billing_plan] : subjects.filter((s) => s.type === 'sku').map((s) => skuInfo.get(s.ref.toUpperCase())?.billingPlan ?? null))
      .map(billingPlanLabel).filter((x): x is string => !!x),
  ))
  const sources = parseJsonArray<Source>(f.sources_json)
  const basis = parseJsonObject<SavingBasis>(f.saving_basis_json)
  const evidence = parseJsonObject<{ tenant_evidence?: Array<{ claim: string; source: string; observed_at: string | null }>; validation?: string[] }>(f.evidence_json)
  const saving = f.potential_monthly_saving ?? (f.finding_kind ? null : f.estimated_monthly_saving)
  const savingStatus = f.finding_kind ? f.saving_status : (f.estimated_monthly_saving > 0 ? 'estimated_catalog' : 'not_applicable')

  return (
    <Card>
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1 space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant={findingKindVariant[kind] ?? 'neutral'}>{findingKindLabels[kind] ?? kind}</Badge>
            {weakEvidence && <Badge variant="warning">Evidencia insuficiente</Badge>}
            <Badge variant="info">{categoryLabels[f.category] ?? f.category}</Badge>
            <Badge variant={savingStatusVariant[savingStatus] ?? 'neutral'}>{savingStatusLabels[savingStatus] ?? savingStatus}</Badge>
            {f.client_reviewed_at && <Badge variant="success">Revisado {formatDateTime(f.client_reviewed_at)}</Badge>}
            {f.license_display_name && <span className="text-xs font-medium" style={{ color: 'var(--color-brand-secondary)' }}>{f.license_display_name}</span>}
          </div>
          <h4 className="font-semibold" style={{ color: 'var(--color-text-primary)' }}>{plain(f.title)}</h4>
          {weakEvidence && (
            <div className="flex items-start gap-2 rounded-md border p-3 text-sm" style={{ borderColor: 'var(--color-warning)', backgroundColor: 'var(--color-warning-light, #FEF3C7)', color: 'var(--color-text-primary)' }}>
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" style={{ color: 'var(--color-warning-dark)' }} />
              <div className="space-y-1">
                <p className="font-medium">Requiere validación por vuestra parte antes de actuar.</p>
                <p className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>El investigador no pudo confirmar toda la evidencia. Este hallazgo no suma en los totales de ahorro hasta que lo validéis.</p>
                {evidence?.validation?.length ? <p className="text-xs">Motivo: {plain(evidence.validation.join(' · '))}</p> : null}
                {parseJsonArray<string>(f.missing_data_json).length > 0 && <p className="text-xs">Qué falta comprobar: {parseJsonArray<string>(f.missing_data_json).map((m) => plain(m)).join(' · ')}</p>}
                {f.next_step && <p className="text-xs">Siguiente paso: {plain(f.next_step)}</p>}
              </div>
            </div>
          )}
          <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>{plain(f.description)}</p>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs" style={{ color: 'var(--color-text-tertiary)' }}>
            {subjects.length > 0 && <span>Afecta a: {subjects.map((s) => `${s.type === 'tenant' ? 'todo el tenant' : s.type === 'sku' ? skuName(skuInfo, s.ref) : s.ref}${s.count !== null ? ` (${s.count})` : ''}`).join(', ')}</span>}
            {billingPlans.length > 0 && <span>Tipo de pago: {billingPlans.join(' · ')}</span>}
            {f.recommended_action && <span>Acción: {actionLabels[f.recommended_action] ?? f.recommended_action}</span>}
            {f.confidence != null && <span className="flex items-center gap-1">Confianza: <ConfidenceBar score={f.confidence} /></span>}
            {f.data_as_of && <span>Datos: {formatDateTime(f.data_as_of)}</span>}
            {f.sources_checked_at && <span>Fuentes: {formatDateTime(f.sources_checked_at)}</span>}
          </div>
          {f.license_sku_id && (
            <p className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>
              <Link to="/subscriptions" search={{ license: f.license_sku_id }} className="underline" style={{ color: 'var(--color-text-link)' }}>Ver usuarios de esta licencia</Link>
            </p>
          )}
          {relatedIds.length > 0 && (
            <p className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>
              {kind === 'confirmation' ? 'Confirma' : 'Complementa'} {relatedIds.length} recomendación(es) por reglas.{' '}
              <Link to="/recommendations" search={{ ids: relatedIds.join(',') }} className="underline" style={{ color: 'var(--color-text-link)' }}>Ver en Recomendaciones</Link>
              {f.beyond_rules && <span> · Aporta: {plain(f.beyond_rules)}</span>}
            </p>
          )}
          <button type="button" onClick={onToggle} className="inline-flex items-center gap-1 text-xs font-medium" style={{ color: 'var(--color-text-link)' }}>
            Detalle, cálculo y evidencias
            <ChevronDown className="h-3.5 w-3.5 transition-transform" style={{ transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)' }} />
          </button>
          {expanded && (
            <div className="grid grid-cols-1 gap-4 rounded-md border p-4 md:grid-cols-2" style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-gray-50)' }}>
              {kind === 'legacy' ? (
                <div className="md:col-span-2 text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                  Hallazgo generado con el formato anterior. Evidencia registrada:
                  <pre className="mt-2 max-h-64 overflow-auto rounded-md border p-3 text-xs" style={{ backgroundColor: 'var(--color-surface)', borderColor: 'var(--color-border)', fontFamily: 'var(--font-mono)' }}>{f.evidence_json ?? '—'}</pre>
                </div>
              ) : (
                <>
                  <Section title="Situación actual">{plain(f.current_state) || '—'}</Section>
                  <Section title="Propuesta a estudiar">{plain(f.proposal) || '—'}</Section>
                  <Section title="Cálculo económico">
                    {basis ? (
                      <div className="space-y-1">
                        {basis.current && <p>SKU actual: {basis.current.displayName ?? basis.current.skuPartNumber} · {basis.current.price !== null ? `${formatCurrency(basis.current.price)}/unidad/mes (${basis.current.source === 'contract' ? 'precio contractual' : 'catálogo de referencia'})` : 'sin precio interno'}</p>}
                        {basis.proposed && <p>SKU destino: {basis.proposed.displayName ?? basis.proposed.skuPartNumber} · {basis.proposed.price !== null ? `${formatCurrency(basis.proposed.price)}/unidad/mes (${basis.proposed.source === 'contract' ? 'precio contractual' : 'catálogo de referencia'})` : 'sin precio interno'}</p>}
                        <p>Unidades: {f.affected_units ?? '—'} ({basis.units_source === 'agent' ? 'según la investigación' : basis.units_source === 'subjects' ? 'según los sujetos identificados' : basis.units_source === 'related_rules' ? 'según las reglas relacionadas' : 'no sostenidas por datos'})</p>
                        <p>{plain(basis.note)}</p>
                        {f.realizable_monthly_saving !== null && f.realizable_monthly_saving !== f.potential_monthly_saving && <p>Realizable sin compra: {formatCurrency(f.realizable_monthly_saving)}/mes</p>}
                        {basis.economics_note && <p style={{ color: 'var(--color-text-secondary)' }}>{plain(basis.economics_note)}</p>}
                        <BulletList items={parseJsonArray<string>(f.saving_assumptions_json)} empty="Sin supuestos adicionales." skuInfo={skuInfo} />
                      </div>
                    ) : <p style={{ color: 'var(--color-text-tertiary)' }}>{kind === 'confirmation' ? 'El ahorro ya figura en la recomendación por reglas.' : 'Sin cálculo económico.'}</p>}
                  </Section>
                  <Section title="Capacidades">
                    <p className="font-medium">Deben conservarse</p>
                    <BulletList items={parseJsonArray<string>(f.capabilities_to_keep_json)} empty="No indicadas." skuInfo={skuInfo} />
                    <p className="mt-2 font-medium">Se perderían</p>
                    <BulletList items={parseJsonArray<string>(f.services_at_risk_json)} empty="Ninguna identificada." skuInfo={skuInfo} />
                  </Section>
                  <Section title="Evidencia interna (datos del tenant)">
                    {evidence?.tenant_evidence?.length ? (
                      <ul className="list-disc space-y-0.5 pl-5">{evidence.tenant_evidence.map((e, i) => <li key={i}>{plain(e.claim)} <span className="text-xs" style={{ color: 'var(--color-text-tertiary)' }}>(fuente: {evidenceSourceLabels[e.source] ?? plain(e.source)}{e.observed_at ? `, ${formatDateTime(e.observed_at)}` : ''})</span></li>)}</ul>
                    ) : <p style={{ color: 'var(--color-text-tertiary)' }}>Sin evidencia interna registrada.</p>}
                  </Section>
                  <Section title="Fuentes externas">
                    {sources.length === 0 ? <p style={{ color: 'var(--color-text-tertiary)' }}>Sin fuentes externas.</p> : (
                      <ul className="space-y-1.5">
                        {sources.map((s, i) => (
                          <li key={i} className="text-sm">
                            <a href={s.url} target="_blank" rel="noreferrer noopener" className="inline-flex items-center gap-1 underline" style={{ color: 'var(--color-text-link)' }}>{s.title || s.url}<ExternalLink className="h-3 w-3" /></a>
                            <span className="ml-2 inline-flex gap-1">
                              <Badge variant={s.verified ? 'success' : 'warning'}>{s.verified ? 'Verificada' : 'No verificada'}</Badge>
                              {s.official && <Badge variant="info">Oficial Microsoft</Badge>}
                            </span>
                            <p className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>{plain(s.claim)} <span style={{ color: 'var(--color-text-tertiary)' }}>· {s.publisher} · consultada {s.accessed_at}</span></p>
                          </li>
                        ))}
                      </ul>
                    )}
                  </Section>
                  <Section title="Incertidumbres y riesgos">
                    <p className="font-medium">Datos que faltan</p>
                    <BulletList items={parseJsonArray<string>(f.missing_data_json)} empty="Ninguno declarado." skuInfo={skuInfo} />
                    <p className="mt-2 font-medium">Riesgos y dependencias</p>
                    <BulletList items={[...parseJsonArray<string>(f.risks_json), ...parseJsonArray<string>(f.dependencies_json)]} empty="Ninguno declarado." skuInfo={skuInfo} />
                    <p className="mt-2 font-medium">Inferencias del investigador</p>
                    <BulletList items={parseJsonArray<string>(f.inferences_json)} empty="Ninguna." skuInfo={skuInfo} />
                    {f.confidence_reason && <p className="mt-2 text-xs" style={{ color: 'var(--color-text-tertiary)' }}>Confianza: {plain(f.confidence_reason)}</p>}
                  </Section>
                  <Section title="Siguiente acción recomendada">{plain(f.next_step) || '—'}</Section>
                  {evidence?.validation?.length ? (
                    <Section title="Notas de validación de ROI365"><BulletList items={evidence.validation} skuInfo={skuInfo} /></Section>
                  ) : null}
                </>
              )}
            </div>
          )}
          {f.client_review_note && <p className="text-xs" style={{ color: 'var(--color-text-tertiary)' }}>Nota de revisión: {f.client_review_note}</p>}
        </div>

        <div className="flex shrink-0 flex-col items-end gap-3">
          {saving !== null && saving > 0 ? (
            <p className="text-lg font-bold tabular-nums" style={{ color: savingStatus === 'computed' ? 'var(--color-brand-accent-2)' : 'var(--color-text-secondary)' }}>
              {formatCurrency(saving)}<span className="text-xs font-normal" style={{ color: 'var(--color-text-tertiary)' }}>/mes{savingStatus === 'estimated_catalog' ? ' (est.)' : ''}</span>
            </p>
          ) : (
            <p className="text-xs" style={{ color: 'var(--color-text-tertiary)' }}>{savingStatusLabels[savingStatus] ?? 'Sin cuantificar'}</p>
          )}
          {canWrite && (canMarkReviewed || canDecide) && (
            <div className="flex flex-wrap justify-end gap-1.5">
              {canMarkReviewed && <Button variant="secondary" size="sm" onClick={onReview} loading={busy.review}><ClipboardCheck className="h-3.5 w-3.5" /> Revisado</Button>}
              {canDecide && (
                <>
                  <Button variant="primary" size="sm" onClick={onPromote} loading={busy.promote}><Check className="h-3.5 w-3.5" /> Promover</Button>
                  <Button variant="secondary" size="sm" onClick={onApply} loading={busy.apply}><Check className="h-3.5 w-3.5" /> Aplicado</Button>
                  <Button variant="ghost" size="sm" onClick={onDismiss} loading={busy.dismiss}><X className="h-3.5 w-3.5" /> Descartar</Button>
                </>
              )}
            </div>
          )}
          <Button variant="secondary" size="sm" onClick={onAsk}><MessageSquare className="h-3.5 w-3.5" /> Preguntar al investigador</Button>
          {canGenerate && f.research_task_key && (
            <Button variant="ghost" size="sm" onClick={onReinvestigate} loading={busy.reinvestigate}><RefreshCw className="h-3.5 w-3.5" /> Repetir análisis</Button>
          )}
          {isPendingReview && (
            <div className="text-right text-xs" style={{ color: 'var(--color-text-tertiary)' }}>
              <span className="italic">{adminPendingLabel[f.status]}</span>
              {evidence?.validation?.length ? <p className="mt-0.5" style={{ color: 'var(--color-warning-dark)' }}>Motivo: {plain(evidence.validation.join(' · '))}</p> : null}
            </div>
          )}
        </div>
      </div>
    </Card>
  )
}
